/**
 * BetriebsspiegelView — One-page executive farm summary.
 * Phase 1 Sprint 5.
 *
 * Props: token
 */
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export default function BetriebsspiegelView({ token }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch('/api/v1/aif/reports/betriebsspiegel', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setData(await res.json());
    } catch (_) { /* network error — UI already shows prior state */ }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const res = await fetch('/api/v1/aif/reports/betriebsspiegel/pdf', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Betriebsspiegel_${new Date().toISOString().slice(0, 10)}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (_) { /* network error — UI already shows prior state */ }
    setDownloading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-7 h-7 border-4 border-[var(--aif-border-default)] border-t-[var(--aif-color-primary)] rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12 px-6">
        <div className="text-4xl mb-3">📊</div>
        <p className="text-sm text-[var(--aif-text-muted)]">{t('betriebsspiegel.noData')}</p>
      </div>
    );
  }

  const { betrieb, land_overview, crop_portfolio, machinery, financial_summary, key_metrics } = data;
  const lo = land_overview || {};
  const fs = financial_summary || {};
  const km = key_metrics || {};

  return (
    <div className="space-y-4 px-4 py-4" data-testid="betriebsspiegel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[var(--aif-text-primary)]">{t('betriebsspiegel.title')}</h2>
          <div className="text-xs text-[var(--aif-text-muted)]">
            {betrieb?.name} · {betrieb?.region} · {t('betriebsspiegel.asOf')} {new Date().toLocaleDateString()}
          </div>
        </div>
        <button onClick={handleDownloadPDF} disabled={downloading}
          className="px-3 py-2 rounded-xl bg-[var(--aif-color-primary)] text-white text-xs font-medium hover:opacity-90 transition min-h-[44px]"
          data-testid="pdf-download-btn">
          {downloading ? '...' : 'PDF'}
        </button>
      </div>

      {/* Land overview */}
      <Section title={t('betriebsspiegel.landTitle')}>
        <div className="text-lg font-bold text-[var(--aif-text-primary)]">{lo.total_ha} ha</div>
        <div className="text-xs text-[var(--aif-text-muted)] space-y-0.5">
          <div>{lo.ackerland_ha} ha {t('betriebsspiegel.arable')} ({lo.total_ha ? Math.round(lo.ackerland_ha / lo.total_ha * 100) : 0}%)</div>
          <div>{lo.gruenland_ha} ha {t('betriebsspiegel.grassland')} ({lo.total_ha ? Math.round(lo.gruenland_ha / lo.total_ha * 100) : 0}%)</div>
          <div>{lo.fields_count} {t('betriebsspiegel.fields')} · {lo.avg_bp ? `Ø ${lo.avg_bp} ${t('betriebsspiegel.soilPoints')}` : ''}</div>
        </div>
      </Section>

      {/* Crop portfolio */}
      {crop_portfolio?.length > 0 && (
        <Section title={t('betriebsspiegel.cropTitle')}>
          <div className="space-y-2">
            {crop_portfolio.map((c, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="flex-1">
                  <div className="flex justify-between text-xs mb-0.5">
                    <span className="text-[var(--aif-text-primary)] font-medium">{c.crop}</span>
                    <span className="text-[var(--aif-text-muted)]">{c.area_ha} ha · {c.pct}%</span>
                  </div>
                  <div className="h-2 bg-[var(--aif-border-default)] rounded-full overflow-hidden">
                    <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${c.pct}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Machinery */}
      {machinery?.length > 0 && (
        <Section title={t('betriebsspiegel.machineryTitle')}>
          <div className="space-y-1">
            {machinery.map((m, i) => (
              <div key={i} className="text-xs text-[var(--aif-text-primary)]">
                {m.name || `${m.brand || ''} ${m.model || ''}`.trim()}
                {m.power_kw && <span className="text-[var(--aif-text-muted)] ml-1">({m.power_kw} kW)</span>}
                {m.year && <span className="text-[var(--aif-text-muted)] ml-1">{m.year}</span>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Financial summary */}
      <Section title={t('betriebsspiegel.financeTitle')}>
        <div className="grid grid-cols-2 gap-3">
          <MetricBox label={t('betriebsspiegel.dbPerHa')} value={`${fs.db_per_ha?.toLocaleString()} EUR`} />
          <MetricBox label={t('betriebsspiegel.revenue')} value={`${fs.total_revenue_eur?.toLocaleString()} EUR`} />
          <MetricBox label={t('betriebsspiegel.subsidies')} value={`~${fs.subsidy_estimate_eur?.toLocaleString()} EUR`} />
          <MetricBox label={t('betriebsspiegel.inventory')} value={`${fs.inventory_value_eur?.toLocaleString()} EUR`} />
        </div>
      </Section>

      {/* Key metrics */}
      <Section title={t('betriebsspiegel.metricsTitle')}>
        <div className="text-xs text-[var(--aif-text-primary)] space-y-1">
          <div>{t('betriebsspiegel.activitiesCount', { count: km.activities_this_year || 0 })}</div>
          <div>{t('betriebsspiegel.tasksCount', { count: km.tasks_completed || 0 })}</div>
        </div>
      </Section>

      {/* PDF download full-width */}
      <button onClick={handleDownloadPDF} disabled={downloading}
        className="w-full py-3 rounded-xl bg-[var(--aif-color-primary)] text-white text-sm font-medium hover:opacity-90 transition min-h-[44px]"
        data-testid="pdf-download-full">
        {downloading ? t('betriebsspiegel.generating') : t('betriebsspiegel.downloadPDF')}
      </button>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-[var(--aif-bg-elevated)] rounded-xl p-4 border border-[var(--aif-border-default)]">
      <div className="text-xs font-medium text-[var(--aif-text-muted)] uppercase tracking-wider mb-2">{title}</div>
      {children}
    </div>
  );
}

function MetricBox({ label, value }) {
  return (
    <div>
      <div className="text-[10px] text-[var(--aif-text-muted)]">{label}</div>
      <div className="text-sm font-semibold text-[var(--aif-text-primary)]">{value}</div>
    </div>
  );
}
